import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, isSameDay, parseISO, isValid } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { Phone, MessageCircle, Mail, MessageSquare, Users, Linkedin, MoreHorizontal, CheckSquare, AlertCircle, Loader2, Plus } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import LogInteractionModal from './LogInteractionModal';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api`
    : (console.warn('REACT_APP_BACKEND_URL is not set; falling back to /api'), '/api');

const CHANNEL_META = {
    call:     { label: 'Call',     icon: Phone,          color: 'bg-blue-100 text-blue-700' },
    whatsapp: { label: 'WhatsApp', icon: MessageCircle,   color: 'bg-green-100 text-green-700' },
    email:    { label: 'Email',    icon: Mail,            color: 'bg-purple-100 text-purple-700' },
    sms:      { label: 'SMS',      icon: MessageSquare,   color: 'bg-orange-100 text-orange-700' },
    meeting:  { label: 'Meeting',  icon: Users,           color: 'bg-red-100 text-red-700' },
    linkedin: { label: 'LinkedIn', icon: Linkedin,        color: 'bg-blue-100 text-blue-800' },
    other:    { label: 'Other',    icon: MoreHorizontal,  color: 'bg-slate-100 text-slate-700' },
};

const OUTCOME_META = {
    interested:          { label: 'Interested',          color: 'bg-green-100 text-green-800' },
    not_interested:      { label: 'Not Interested',      color: 'bg-red-100 text-red-800' },
    no_answer:           { label: 'No Answer',           color: 'bg-slate-100 text-slate-600' },
    callback_requested:  { label: 'Callback Requested',  color: 'bg-yellow-100 text-yellow-800' },
    follow_up_needed:    { label: 'Follow-up Needed',    color: 'bg-blue-100 text-blue-800' },
    deal_closed:         { label: 'Deal Closed',         color: 'bg-emerald-100 text-emerald-800' },
};

const TASK_TYPE_META = {
    Call:      { icon: Phone,         color: 'bg-blue-100 text-blue-700' },
    WhatsApp:  { icon: MessageCircle, color: 'bg-green-100 text-green-700' },
    Email:     { icon: Mail,          color: 'bg-purple-100 text-purple-700' },
    SMS:       { icon: MessageSquare, color: 'bg-orange-100 text-orange-700' },
    Meeting:   { icon: Users,         color: 'bg-red-100 text-red-700' },
    LinkedIn:  { icon: Linkedin,      color: 'bg-blue-100 text-blue-800' },
    Todo:      { icon: CheckSquare,   color: 'bg-slate-100 text-slate-700' },
};

const safeParseDate = (str) => {
    if (!str) return null;
    try {
        const d = parseISO(str);
        return isValid(d) ? d : null;
    } catch { return null; }
};

const ActivityCalendar = ({ leadId, leadName }) => {
    const [interactions, setInteractions] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(new Date());
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (!leadId) return;
        const controller = new AbortController();
        const fetchData = async () => {
            try {
                setLoading(true);
                const [intRes, taskRes] = await Promise.all([
                    axios.get(`${API_URL}/leads/${leadId}/interactions`, { signal: controller.signal }),
                    axios.get(`${API_URL}/tasks`, { params: { lead_id: leadId }, signal: controller.signal }),
                ]);
                setInteractions(intRes.data);
                setTasks(taskRes.data);
            } catch (e) {
                if (!axios.isCancel(e) && e.name !== 'CanceledError' && e.name !== 'AbortError') {
                    toast.error('Failed to load activity data');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        fetchData();
        return () => controller.abort();
    }, [leadId, refreshToken]);

    // Build map: dateString → { interactions: [], tasks: [] }
    const eventsByDate = useMemo(() => {
        const map = {};
        for (const i of interactions) {
            const d = safeParseDate(i.occurred_at);
            if (!d) continue;
            const key = format(d, 'yyyy-MM-dd');
            if (!map[key]) map[key] = { interactions: [], tasks: [] };
            map[key].interactions.push(i);
        }
        for (const t of tasks) {
            const d = safeParseDate(t.due_date);
            if (!d) continue;
            const key = format(d, 'yyyy-MM-dd');
            if (!map[key]) map[key] = { interactions: [], tasks: [] };
            map[key].tasks.push(t);
        }
        return map;
    }, [interactions, tasks]);

    const selectedKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
    const selectedEvents = selectedKey ? (eventsByDate[selectedKey] || { interactions: [], tasks: [] }) : { interactions: [], tasks: [] };

    // Days with interactions (past) and tasks (upcoming/overdue)
    const interactionDays = interactions.map(i => safeParseDate(i.occurred_at)).filter(Boolean);
    const taskDays = tasks.map(t => safeParseDate(t.due_date)).filter(Boolean);

    const modifiers = {
        hasInteraction: (day) => interactionDays.some(d => isSameDay(d, day)),
        hasTask: (day) => taskDays.some(d => isSameDay(d, day)),
    };

    const modifiersClassNames = {
        hasInteraction: 'rdp-day--has-interaction',
        hasTask: 'rdp-day--has-task',
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="flex gap-6 h-full">
            {/* Calendar column */}
            <div className="flex-shrink-0">
                <style>{`
                    .rdp-day--has-interaction:not([aria-selected]) { position: relative; }
                    .rdp-day--has-interaction:not([aria-selected])::after {
                        content: '';
                        position: absolute;
                        bottom: 2px; left: 50%; transform: translateX(-50%);
                        width: 4px; height: 4px;
                        border-radius: 50%;
                        background: #3b82f6;
                    }
                    .rdp-day--has-task:not([aria-selected])::before {
                        content: '';
                        position: absolute;
                        bottom: 2px; left: calc(50% + 4px);
                        width: 4px; height: 4px;
                        border-radius: 50%;
                        background: #f97316;
                    }
                    .rdp-day--has-interaction.rdp-day--has-task:not([aria-selected])::after {
                        left: calc(50% - 4px);
                    }
                `}</style>
                <DayPicker
                    mode="single"
                    selected={selectedDay}
                    onSelect={setSelectedDay}
                    modifiers={modifiers}
                    modifiersClassNames={modifiersClassNames}
                    className="border rounded-xl p-3 bg-white shadow-sm"
                />
                <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 px-1">
                    <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
                        Interaction
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />
                        Task due
                    </span>
                </div>
                <div className="mt-4">
                    <Button size="sm" className="w-full" onClick={() => setLogModalOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Log Interaction
                    </Button>
                </div>
            </div>

            {/* Day detail panel */}
            <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {selectedDay ? format(selectedDay, 'EEEE, MMMM d, yyyy') : 'Select a date'}
                </h3>

                {selectedEvents.interactions.length === 0 && selectedEvents.tasks.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                        <p className="text-sm">No activity on this day</p>
                    </div>
                ) : (
                    <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-320px)] pr-1">
                        {selectedEvents.interactions.map(interaction => {
                            const ch = CHANNEL_META[interaction.channel] || CHANNEL_META.other;
                            const oc = OUTCOME_META[interaction.outcome] || { label: interaction.outcome, color: 'bg-slate-100 text-slate-700' };
                            const Icon = ch.icon;
                            return (
                                <div key={interaction.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${ch.color} flex-shrink-0`}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-slate-800">{ch.label}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${oc.color}`}>{oc.label}</span>
                                                {interaction.duration_minutes && (
                                                    <span className="text-xs text-slate-400">{interaction.duration_minutes} min</span>
                                                )}
                                            </div>
                                            {interaction.notes && (
                                                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{interaction.notes}</p>
                                            )}
                                            <p className="text-xs text-slate-400 mt-1">
                                                {(() => { const d = safeParseDate(interaction.occurred_at); return d ? format(d, 'h:mm a') : '—'; })()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {selectedEvents.tasks.map(task => {
                            const meta = TASK_TYPE_META[task.type] || TASK_TYPE_META.Todo;
                            const Icon = meta.icon;
                            const parsedDueDate = task.due_date ? parseISO(task.due_date) : null;
                            const isOverdue = parsedDueDate && isValid(parsedDueDate)
                                && format(parsedDueDate, 'yyyy-MM-dd') < format(new Date(), 'yyyy-MM-dd')
                                && task.status === 'Pending';
                            return (
                                <div key={task.id} className={`rounded-lg border p-3 shadow-sm ${isOverdue ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${meta.color} flex-shrink-0`}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-slate-800">{task.type} Task</span>
                                                {isOverdue && (
                                                    <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                                                        <AlertCircle className="h-3 w-3" /> Overdue
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-slate-700 mt-0.5">{task.description}</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                Due {(() => { const d = safeParseDate(task.due_date); return d ? format(d, 'h:mm a') : 'Invalid date'; })()} · {task.status}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <LogInteractionModal
                open={logModalOpen}
                onClose={() => setLogModalOpen(false)}
                leadId={leadId}
                leadName={leadName}
                onSaved={() => setRefreshToken(t => t + 1)}
            />
        </div>
    );
};

export default ActivityCalendar;
